import React from "react";
import { useSearchParams } from "react-router-dom";
import { Type, Eye, Zap, FileText, Gift, MousePointerClick, PenLine, LayoutTemplate, ImageIcon, MessageCircle, Reply } from "lucide-react";
import { Card, CardContent } from "../../../components/ui/card";
import SubjectsPanel from "./SubjectsPanel";
import PreviewsPanel from "./PreviewsPanel";
import HooksPanel from "./HooksPanel";
import EmailBodiesPanel from "./EmailBodiesPanel";
import OffersPanel from "./OffersPanel";
import CtasPanel from "./CtasPanel";
import PsPanel from "./PsPanel";
import LandingPagesPanel from "./LandingPagesPanel";
import ImagesPanel from "./ImagesPanel";
import CsmPanel from "./CsmPanel";
import RinPanel from "./RinPanel";

/**
 * AssetsShell — EMAILS-IMPL-009
 *
 * Sub-navigation across the 11 governed reusable Asset families, mounted
 * inside EmailsShell's "Assets" tab (CTA Support Microcopy and Reply
 * Instructions added by EMAILS-IMPL-032, EMAILS-SPEC-017/017B).
 *
 * Navigation pattern: a vertical side-category list, not a second row of
 * horizontal Tabs. EmailsShell's OWN top-level nav already uses a flat
 * Tabs+`?tab=` pattern for its 7 sections (see that file's own comment for
 * why -- it mirrors AnalyticsWarRoom.jsx's primary-section pattern); nine
 * MORE items crammed into a horizontal TabsList here would wrap across
 * several rows and read as visually unrelated to (or worse, confusable
 * with) the outer 7-tab row directly above it. A vertical list, in
 * contrast, reads unambiguously as "nine sibling categories inside
 * Assets" and keeps to this module's own "compact operational layout"
 * visual guidance. State lives in a SEPARATE `?asset=` query param
 * (leaving `?tab=assets` alone) so a reload/deep-link preserves which
 * family sub-view was open, the same query-param-driven-state idea
 * EmailsShell itself already established for its own top-level tab.
 *
 * User-facing labels are the plain-English family names (checkpoint §4:
 * "Do not expose backend acronyms as the only user-facing labels") --
 * governed IDs (SUB0001, PRV0001, ...) remain visible inside each panel's
 * own table/detail view, never hidden.
 */
const FAMILIES = [
  { value: "subjects", label: "Subject Lines", code: "SUB", icon: Type, Component: SubjectsPanel },
  { value: "previews", label: "Preview Text", code: "PRV", icon: Eye, Component: PreviewsPanel },
  { value: "hooks", label: "Hooks", code: "HOK", icon: Zap, Component: HooksPanel },
  { value: "email-bodies", label: "Email Bodies", code: "EML", icon: FileText, Component: EmailBodiesPanel },
  { value: "offers", label: "Offers", code: "OFF", icon: Gift, Component: OffersPanel },
  { value: "ctas", label: "CTAs", code: "CTA", icon: MousePointerClick, Component: CtasPanel },
  { value: "cta-support-microcopy", label: "CTA Support Microcopy", code: "CSM", icon: MessageCircle, Component: CsmPanel },
  { value: "ps", label: "P.S.", code: "PS", icon: PenLine, Component: PsPanel },
  { value: "reply-instructions", label: "Reply Instructions", code: "RIN", icon: Reply, Component: RinPanel },
  { value: "landing-pages", label: "Landing Pages", code: "LPG", icon: LayoutTemplate, Component: LandingPagesPanel },
  { value: "images", label: "Images", code: "IMG", icon: ImageIcon, Component: ImagesPanel },
];

const DEFAULT_FAMILY = "subjects";
const FAMILY_VALUES = new Set(FAMILIES.map((f) => f.value));

export default function AssetsShell() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("asset");
  const active = FAMILY_VALUES.has(requested) ? requested : DEFAULT_FAMILY;
  const activeFamily = FAMILIES.find((f) => f.value === active);

  const selectFamily = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value === DEFAULT_FAMILY) next.delete("asset");
    else next.set("asset", value);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-[220px_1fr]" data-testid="assets-shell">
      <Card className="glass h-fit border-white/10">
        <CardContent className="p-2">
          <nav className="flex flex-row flex-wrap gap-1 md:flex-col" data-testid="assets-nav" aria-label="Asset families">
            {FAMILIES.map((family) => {
              const Icon = family.icon;
              const isActive = family.value === active;
              return (
                <button
                  key={family.value}
                  type="button"
                  onClick={() => selectFamily(family.value)}
                  data-testid={`assets-nav-${family.value}`}
                  aria-current={isActive ? "true" : undefined}
                  className={`flex items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors ${
                    isActive ? "bg-violet-500/20 text-violet-200" : "text-slate-300 hover:bg-white/5"
                  }`}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="flex-1">{family.label}</span>
                  <span className="font-mono text-[10px] text-slate-500">{family.code}</span>
                </button>
              );
            })}
          </nav>
        </CardContent>
      </Card>

      <div data-testid={`assets-content-${active}`}>
        {activeFamily ? <activeFamily.Component /> : null}
      </div>
    </div>
  );
}
